import { useDateRange } from '../../../../store/zustand/dateRangeStore'
import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { DateRange } from '../../../../lib/types/DateRange'
import { Link, useSearchParams } from 'react-router-dom'
import { useDownloadPDFReport } from '../../../../api/dealership/reports/useDownloadPDFReport'
import PageHeader from '../../../../components/PageHeader/PageHeader'
import {
  Button,
  Info,
  StatsCard,
  Table,
  Tooltip,
  MonthPicker,
  Toast,
} from '@demandlocal/components'
import toast from 'react-hot-toast'
import mtm from '../../../../lib/utils/mtm'
import { GA4 } from '../../../../lib/utils/ga4'
import { ReactComponent as DownloadIcon } from '../../../../images/icons/action_download.svg'
import { useDealership } from '../../../../store/zustand/dealershipStore'
import { createColumnHelper } from '@tanstack/react-table'
import { Campaign } from '../../../../lib/types/Campaign'
import dayjs from 'dayjs'
import { useGetAgencyByAgencyId } from '../../../../api/agencies/useGetAgencyByAgencyId'
import { formatCurrency, formatNumber } from '../../../../lib/utils/formatNumber'
import { useGetDealershipConversionGoalsByDealershipID } from '../../../../api/dealership/audiences/useGetConversionGoalsByDealershipID'
import { useGetCampaignsSummaryList } from '../../../../api/dealership/campaigns/useGetCampaignsSummaryList'
import { useTranslation } from 'react-i18next'
import KPICard from '../../../../components/Overview/KPICard'
import { ReactComponent as LeadsIcon } from '../../../../images/icons/email_icon.svg'
import { ReactComponent as ClickToCallIcon } from '../../../../images/icons/phone_icon.svg'
import { ReactComponent as ClickToChatIcon } from '../../../../images/icons/click_to_chat_icon.svg'
import { useGetCampaignsSummaryReport } from '../../../../api/dealership/campaigns/reports/useGetCampaignsSummaryReport'
import { useGetTotalBudget } from '../../../../api/dealership/profile/useGetTotalBudget'
import { take } from 'lodash'
import { useGetConversionsSummary } from '../../../../api/dealership/performance/conversions/useGetConversionsSummary'
import MultiLineChart from '../../../../components/Charts/MultiLineChart'
import { DisplayCreative } from '../../../../lib/types/DisplayCreativeMetrics'
import { useGetCreativesConversions } from '../../../../api/dealership/performance/conversions/useGetCreativesConversions'
import { useGetConversionPathsTrackingTags } from '../../../../api/dealership/performance/conversionPaths/useGetConversionPathsTrackingTags'
import { useGetPhoneCalls } from '../../../../api/dealership/profile/useGetPhoneCalls'

const columnHelper = createColumnHelper<Campaign>()
const creativesColumnHelper = createColumnHelper<DisplayCreative>()

const AudienceTemplate = ({
  children,
  filters,
  setFilters,
  isLoading,
}: PropsWithChildren<{
  filters: DateRange
  setFilters: Dispatch<SetStateAction<DateRange>>
  isLoading: boolean
}>): JSX.Element => {
  const { mutateAsync: downloadReport, isLoading: isDownloadingReport } =
    useDownloadPDFReport()
  const { setDateRange } = useDateRange()
  const dealership = useDealership()

  useEffect(() => {
    if (!isLoading) {
      const content = document.getElementById('content')
      if (content) content.className = `${content.className} loaded`
    }
  }, [isLoading])
  const onDateRangeChange = (dateRange: DateRange): void => {
    setFilters((prev: DateRange) => ({
      ...prev,
      ...dateRange,
    }))
    setDateRange(dateRange)
  }

  return (
    <div className={'flex flex-1 flex-col h-full gap-3'} id={'content'}>
      <PageHeader
        title={`${dealership?.dealership} - Conversion Attribution`}
        rightSlot={() => (
          <div className={'flex flex-row gap-2 items-center'}>
            <MonthPicker
              maxDate={dayjs().toDate()}
              onChange={(date) => {
                const dateRange = {
                  dateRange: {
                    startDate: dayjs(date).startOf('month').format('YYYY-MM-DD'),
                    endDate: dayjs(date).endOf('month').format('YYYY-MM-DD'),
                  },
                }
                onDateRangeChange(dateRange)
              }}
            />
            <div className="print:hidden">
              <Button
                disabled={isDownloadingReport}
                leftSlot={<DownloadIcon className={'fill-white'} />}
                animate={false}
                onClick={async (e) => {
                  e.preventDefault()
                  const toastId = toast.loading('Generating report...')
                  GA4.sendEvent({
                    event_name: 'export_pdf',
                    event_params: {
                      campaign_type: 'conversions',
                    },
                  })
                  mtm.sendEvent('Export', 'PDF', 'conversions')
                  await downloadReport(undefined, {
                    async onSuccess(data) {
                      toast.success('Report generated successfully', { id: toastId })
                      const blob = await data.blob()
                      const url = window.URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = `conversions_insights.pdf`
                      document.body.appendChild(a) // we need to append the element to the dom -> otherwise it will not work in firefox
                      a.click()
                      a.remove()
                    },
                  })
                }}>
                Export as PDF
              </Button>
            </div>
          </div>
        )}
      />
      <div className={'flex flex-1 flex-col px-10 max-w-[1920px] mx-auto w-full'}>
        {children}
      </div>
    </div>
  )
}

const ConversionsReport = (): JSX.Element => {
  const dealership = useDealership()
  const [queryParams, setQueryParams] = useSearchParams()
  const { dateRange } = useDateRange()
  const { data: agency } = useGetAgencyByAgencyId(dealership?.organization_id)
  const { data: tag_names } = useGetDealershipConversionGoalsByDealershipID()
  const { t } = useTranslation('routes')
  const [filters, setFilters] = useState<DateRange>({
    dateRange: {
      startDate: queryParams.get('startDate') || dateRange.dateRange.startDate,
      endDate: queryParams.get('endDate') || dateRange.dateRange.endDate,
    },
  })
  useEffect(() => {
    setQueryParams(filters.dateRange)
  }, [filters, setQueryParams])

  const {
    data: { conversions } = { conversions: [] },
    data: { metrics } = { metrics: [] },
    data: { uniqueSiteVisitors } = { uniqueSiteVisitors: [] },
    isLoading: isLoadingReport,
  } = useGetCampaignsSummaryReport({
    filters: {
      ...filters.dateRange,
    },
  })

  const { data: totalBudget = { data: 0, title: '' }, isLoading: isLoadingTotalBudget } =
    useGetTotalBudget({
      filters: {
        start_date: filters.dateRange.startDate,
        end_date: filters.dateRange.endDate,
      },
    })

  const { data: monthlyConversions, isLoading: isLoadingMonthlyConversions } =
    useGetConversionsSummary({
      filters: {
        startDate: dayjs(filters.dateRange.startDate)
          .startOf('month')
          .subtract(2, 'month')
          .format('YYYY-MM-DD'),
        endDate: dayjs(filters.dateRange.endDate).format('YYYY-MM-DD'),
      },
    })

  const { data: { data: phoneCalls } = {} } = useGetPhoneCalls({
    filters: {
      ...filters.dateRange,
    },
  })

  const { data: trackingTags, isLoading: isLoadingTrackingTags } =
    useGetConversionPathsTrackingTags()

  const totalImpressions = useMemo(() => {
    return metrics?.reduce((acc, cur) => acc + cur.impressions, 0)
  }, [metrics])

  const totalSiteVisitors = useMemo(() => {
    return uniqueSiteVisitors?.reduce((acc, cur) => acc + cur.last_campaign_uniques, 0)
  }, [uniqueSiteVisitors])

  const defaultConversions = useMemo(() => {
    return {
      leads: conversions?.find((d) => d.id === 1),
      click_to_call: conversions?.find((d) => d.id === 11),
      click_to_chat: conversions?.find((d) => d.id === 28),
    }
  }, [conversions])

  const columns = useMemo(() => {
    const baseColumns = [
      columnHelper.accessor('start_date', {
        header: 'Booking Month',
        cell: (info) => (
          <Link
            className={'text-primary-persian-blue font-bold'}
            to={t('dealership.campaigns.report', {
              dealership,
              campaign: {
                product_code: info.row.original.product_code,
                client_campaign_id: info.row.original.client_campaign_id,
                start_date: dateRange.dateRange.startDate,
                end_date: dateRange.dateRange.endDate,
              },
            })}>
            {dayjs(info.getValue()).format("MMMM 'YY") || '-'}
          </Link>
        ),
        footer: ({ table }) =>
          `Total for ${table.getFilteredRowModel().rows.length} campaigns`,
      }),
      columnHelper.accessor('client_campaign_display_name', {
        header: 'Campaign',
        cell: (info) => info.getValue(),
      }),
      ...(Boolean(agency?.show_budget_type)
        ? [
            columnHelper.display({
              header: 'Budget',
              id: 'budget',
              cell: ({ row }) =>
                agency?.show_budget_type === 1
                  ? formatCurrency(row.original.demand_local_net)
                  : formatCurrency(row.original.budget),
            }),
          ]
        : []),
      columnHelper.accessor('impressions', {
        header: 'Impressions',
        cell: (info) => new Intl.NumberFormat('en-US').format(info.getValue() || 0),
        footer: ({ table }) =>
          formatNumber(
            table
              .getFilteredRowModel()
              .rows.reduce(
                (total, row) => total + Number(row.getValue('impressions')),
                0,
              ),
          ),
      }),
      columnHelper.accessor('clicks', {
        header: 'Clicks',
        cell: (info) => new Intl.NumberFormat('en-US').format(info.getValue() || 0),
        footer: ({ table }) =>
          formatNumber(
            table
              .getFilteredRowModel()
              .rows.reduce((total, row) => total + Number(row.getValue('clicks')), 0),
          ),
      }),
      columnHelper.display({
        id: 'default_conversions',
        header: () => {
          return (
            <div className="flex flex-row gap-2 items-center">
              <span>Conversions</span>
              <Info
                message={`Sum of ${Object.values(defaultConversions)
                  ?.map((tag) => tag?.tag_name)
                  ?.join(', ')}`}
              />
            </div>
          )
        },
        cell: ({ row }) => {
          return Object.values(defaultConversions).reduce(
            (acc, cur) => acc + Number(row.original[cur?.tag_name as string] || 0),
            0,
          )
        },
      }),
    ]
    if (tag_names) {
      baseColumns.push(
        ...tag_names
          ?.filter(
            (tag) =>
              !Object.values(defaultConversions)
                ?.map((c) => c?.id)
                .includes(tag.id),
          )
          .map((tag) => {
            return columnHelper.display({
              header: (info) => {
                return (
                  <div className="flex flex-row gap-1 items-center">{tag.tag_name}</div>
                )
              },
              id: tag.tag_name,
              cell: (info) =>
                info.row.original[tag.tag_name] ||
                (['MSE', 'SEM', 'VLA', 'TRV'].includes(info.row.original.product_code) ? (
                  <span>N/A</span>
                ) : (
                  <Tooltip label={'Site attribution not configured for this campaign'}>
                    <span>N/A</span>
                  </Tooltip>
                )),
              footer: ({ table }) =>
                formatNumber(
                  table
                    .getFilteredRowModel()
                    .rows.reduce(
                      (total, row) => total + (Number(row.original[tag.tag_name]) || 0),
                      0,
                    ),
                ),
            })
          }),
      )
    }
    baseColumns.push(
      ...[
        columnHelper.accessor('sales', {
          header: 'Attributed sales',
          cell: (info) => info.getValue() || 0,
        }),
      ],
    )
    return baseColumns
  }, [
    agency?.show_budget_type,
    dateRange.dateRange.endDate,
    dateRange.dateRange.startDate,
    dealership,
    defaultConversions,
    t,
    tag_names,
  ])

  const creativesColumns = useMemo(() => {
    const baseColumns = [
      creativesColumnHelper.accessor('creative_name', {
        header: 'Creative',
        cell: (info) => info.getValue(),
      }),
      creativesColumnHelper.accessor('creative_size', {
        header: 'Size',
        cell: (info) => info.getValue(),
      }),
      ...(!tag_names?.find((t) => t.id === 4)
        ? [
            creativesColumnHelper.display({
              id: 'APV',
              header: 'All Page Views',
              cell: ({ row }) => formatNumber(row.original['All Page Views'] || 0),
            }),
          ]
        : []),
      creativesColumnHelper.display({
        id: 'default_conversions',
        header: () => {
          return (
            <div className="flex flex-row gap-2 items-center">
              <span>Conversions</span>
              <Info
                message={`Sum of ${Object.values(defaultConversions)
                  ?.map((tag) => tag?.tag_name)
                  ?.join(', ')}`}
              />
            </div>
          )
        },
        cell: ({ row }) => {
          return Object.values(defaultConversions).reduce(
            (acc, cur) => acc + Number(row.original[cur?.tag_name as string] || 0),
            0,
          )
        },
      }),
    ]
    if (tag_names) {
      baseColumns.push(
        ...tag_names
          ?.filter(
            (tag) =>
              !Object.values(defaultConversions)
                ?.map((c) => c?.id)
                .includes(tag.id),
          )
          .map((tag) => {
            return creativesColumnHelper.display({
              header: (info) => {
                return (
                  <div className="flex flex-row gap-1 items-center">{tag.tag_name}</div>
                )
              },
              id: tag.tag_name,
              cell: (info) => info.row.original[tag.tag_name],
              footer: ({ table }) =>
                formatNumber(
                  table
                    .getFilteredRowModel()
                    .rows.reduce(
                      (total, row) => total + (Number(row.original[tag.tag_name]) || 0),
                      0,
                    ),
                ),
            })
          }),
      )
    }
    return baseColumns
  }, [defaultConversions, tag_names])

  const isLoading = useMemo(
    () =>
      isLoadingReport ||
      isLoadingTotalBudget ||
      isLoadingMonthlyConversions ||
      isLoadingTrackingTags,
    [
      isLoadingMonthlyConversions,
      isLoadingReport,
      isLoadingTotalBudget,
      isLoadingTrackingTags,
    ],
  )

  return (
    <AudienceTemplate filters={filters} setFilters={setFilters} isLoading={isLoading}>
      <div className="py-5 flex flex-col gap-6 w-full font-Spartan">
        <span className="text-md py-3">
          Review conversion totals and metrics from all campaigns. This combines metrics
          across platform when possible, including cost-per-acquisition and ROI, across
          all campaigns.
        </span>
        <div className={`grid grid-cols-${totalBudget?.title?.length > 0 ? 3 : 2} gap-5`}>
          {totalBudget?.title?.length > 0 && (
            <KPICard
              title={'Total Client Spend'}
              color={'blue'}
              content={
                <span className="font-bold text-xl">
                  {formatCurrency(totalBudget?.data || 0)}
                </span>
              }
              isLoading={isLoadingReport}
            />
          )}
          <KPICard
            title={'Total Impressions'}
            color={'green'}
            content={
              <span className="font-bold text-xl">
                {formatNumber(totalImpressions || 0)}
              </span>
            }
            isLoading={isLoadingReport}
          />
          <KPICard
            title={'Total Attributed Site Visitors'}
            color={'orange'}
            content={
              <span className="font-bold text-xl">
                {formatNumber(totalSiteVisitors || 0)}
              </span>
            }
            isLoading={isLoadingReport}
          />
        </div>
        <div className={'flex grid grid-cols-3 gap-5'}>
          <div className={'rounded-xl h-[100px] items-center flex'}>
            <StatsCard
              title={'Leads'}
              content={formatNumber(defaultConversions?.leads?.conversions || 0)}
              color={'bg-chrysler-blue/20'}
              border={false}
              rightSlot={<LeadsIcon />}
              hasAnimatedDescriptionOverflow={true}
              description={
                dealership?.enable_path_to_conversion_report
                  ? 'See the path that led to your leads'
                  : undefined
              }
              info="Includes programmatic, search, and social leads"
              to={
                dealership?.enable_path_to_conversion_report
                  ? `${t('dealership.reports.conversion_path', {
                      dealership,
                    })}?${trackingTags
                      ?.filter((tag) =>
                        tag.conversion_tracking_tag_name?.includes('[LED]'),
                      )
                      ?.map((tag) => `tag=${tag.conversion_tracking_tag_id}`)
                      ?.join('&')}`
                  : undefined
              }
            />
          </div>
          <div className={'rounded-xl h-[100px] items-center flex'}>
            <StatsCard
              title={'Click to Calls / Chat'}
              content={
                <div>
                  {formatNumber(
                    (defaultConversions?.click_to_call?.conversions ?? 0) || 0,
                  )}{' '}
                  /{' '}
                  {formatNumber(
                    (defaultConversions?.click_to_chat?.conversions ?? 0) || 0,
                  )}
                </div>
              }
              color={'bg-champagne-pink'}
              border={false}
              rightSlot={<ClickToChatIcon />}
              hasAnimatedDescriptionOverflow={true}
              description={
                dealership?.enable_path_to_conversion_report
                  ? 'See the path that led to your chats & calls'
                  : undefined
              }
              to={
                dealership?.enable_path_to_conversion_report
                  ? `${t('dealership.reports.conversion_path', {
                      dealership,
                    })}?${trackingTags
                      ?.filter(
                        (tag) =>
                          tag.conversion_tracking_tag_name?.includes('[CHT]') ||
                          tag.conversion_tracking_tag_name?.includes('[CTC]'),
                      )
                      ?.map((tag) => `tag=${tag.conversion_tracking_tag_id}`)
                      ?.join('&')}`
                  : undefined
              }
            />
          </div>
          <div className={'rounded-xl h-[100px] items-center flex'}>
            <StatsCard
              title={'Phone Calls'}
              content={formatNumber(phoneCalls || 0)}
              color={'bg-light-cyan'}
              border={false}
              rightSlot={<ClickToCallIcon />}
              info={'Includes search calls'}
            />
          </div>
        </div>
        <div className={'flex grid grid-cols-3 gap-5'}>
          {take(
            [
              ...(dealership?.attribution_tracking?.goals?.filter(
                (goal) =>
                  !Object.values(defaultConversions)
                    ?.map((c) => c?.id)
                    .includes(goal),
              ) ?? []),
              ...(Array.from({ length: 3 }, () => Math.random()) as number[]),
            ]?.map((goal, idx, arr) => {
              const attributionGoals = arr.length - 3
              const indexStart = idx - attributionGoals
              const nextConversion = conversions
                ?.sort((a, b) => (a.conversions > b.conversions ? -1 : 1))
                ?.slice(indexStart)
                ?.find(
                  (conversion) =>
                    ![
                      ...(dealership?.attribution_tracking?.goals ?? []),
                      ...Object.values(defaultConversions)?.map((c) => c?.id),
                    ].includes(conversion.id),
                )

              return (
                <span className={'rounded-xl'} key={goal}>
                  {goal < 1 ||
                  Object.values(defaultConversions)
                    ?.map((c) => c?.id)
                    .includes(goal) ? (
                    <StatsCard
                      title={nextConversion?.tag_name}
                      content={formatNumber(Math.round(nextConversion?.conversions || 0))}
                      color={'bg-white'}
                      border
                    />
                  ) : (
                    <StatsCard
                      title={
                        conversions?.find((conversion) => conversion.id === goal)
                          ?.tag_name
                      }
                      content={formatNumber(
                        Math.round(
                          conversions?.find((conversion) => conversion.id === goal)
                            ?.conversions || 0,
                        ),
                      )}
                      color={'bg-white'}
                      border
                    />
                  )}
                </span>
              )
            }),
            3,
          )}
        </div>
        {totalBudget?.title?.length > 0 && (
          <div className="grid grid-cols-3 gap-5">
            <StatsCard
              title={'CPA'}
              content={formatCurrency(
                (totalBudget?.data ?? 0) /
                  Object.values(defaultConversions)?.reduce(
                    (acc, cur) => acc + (cur?.conversions ?? 0),
                    0,
                  ),
              )}
              color={'bg-white'}
              border
              info={'Cost per lead, call, or chat'}
            />
            <StatsCard
              title={'CPC'}
              content={formatCurrency(
                (totalBudget?.data ?? 0) /
                  metrics?.reduce((acc, cur) => acc + cur.clicks, 0),
              )}
              color={'bg-white'}
              border
              info={'Cost per click'}
            />
            <StatsCard
              title={'Cost per Site Visitor'}
              content={formatCurrency((totalBudget?.data || 0) / totalSiteVisitors || 0)}
              color={'bg-white'}
              border
            />
          </div>
        )}
        <Toast
          className={'p-3 font-bold items-center'}
          variant={'basic'}
          text={'Conversions have a lookback window of 90 days after the last impression'}
        />
        <div className={'min-h-[270px] border-2 rounded-xl'}>
          <div
            className={
              'pt-5 pb-2 px-5 flex font-bold text-sm text-primary-xiketic justify-between items-center'
            }>
            <div className="flex flex-row gap-2 items-center">
              <span>Previous 3 Month Conversions</span>
            </div>
          </div>
          <div className={'h-[270px] p-2'}>
            <MultiLineChart
              data={
                monthlyConversions?.data
                  ?.filter((conversion) =>
                    [
                      ...Object.values(defaultConversions)?.map((c) => c?.id),
                      ...(tag_names?.map((d) => d.id) ?? []),
                    ].includes(conversion.id),
                  )
                  ?.map((d) => {
                    return {
                      date: d.date,
                      tag: d.tag_name,
                      hits: d.conversions,
                    }
                  })
                  ?.sort((a, b) => (dayjs(a.date).isAfter(dayjs(b.date)) ? 1 : -1)) ?? []
              }
              customOptions={{
                elements: {
                  line: {
                    tension: 0,
                  },
                  point: {
                    radius: 0,
                  },
                },
                plugins: {
                  legend: {
                    position: 'bottom',
                  },
                  tooltip: {
                    mode: 'index' as const,
                    intersect: false,
                  },
                },
                datalabels: {
                  formatter: (value: string): string => dayjs(value).format('MMMM'),
                },
              }}
              gradient={[
                'blue-to-purple',
                'red-to-orange',
                'green-to-cyan',
                'green-to-lemon-green',
                'blue-to-cyan',
                'blue-to-purple',
                'red-to-orange',
                'green-to-cyan',
                'green-to-lemon-green',
                'blue-to-cyan',
              ]}
              borderColor={'white'}
              groupKey={'tag'}
              labelKey={'tag'}
              yKey={'hits'}
              xKey={'date'}
              isLoading={isLoadingMonthlyConversions}
              customLabel={(value) => dayjs(value).format("MMM 'YY")}
            />
          </div>
        </div>
        <div className="flex flex-col gap-8 pb-5">
          <div className="flex flex-col gap-3">
            <span className={'font-bold text-primary-xiketic text-md'}>Campaigns</span>
            <Table
              hidePagination={true}
              emptyLabel={'No campaigns found'}
              columns={columns}
              filters={{ ...filters.dateRange }}
              fetchFn={useGetCampaignsSummaryList}
            />
          </div>
          <div className="flex flex-col gap-3">
            <span className={'font-bold text-primary-xiketic text-md'}>
              Top Converting Creatives
            </span>
            <Table
              hidePagination={true}
              emptyLabel={'No creatives found'}
              columns={creativesColumns}
              filters={{ ...filters.dateRange, hasConversions: 1 }}
              fetchFn={useGetCreativesConversions}
            />
          </div>
        </div>
      </div>
    </AudienceTemplate>
  )
}

export default ConversionsReport
